import RouteTemplate from 'ember-route-template';

import { Input, Textarea } from '@ember/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import GRecaptcha from 'ember-g-recaptcha/components/g-recaptcha';
import LinksTo from 'ember-route-state/components/links-to.gjs';
import MultiselectCheckboxes from 'ember-multiselect-checkboxes/components/multiselect-checkboxes';
import { pageTitle } from 'ember-page-title';

import CButton from 'rise-ui/components/c-button.gjs';
import CIcon from 'rise-ui/components/c-icon.gjs';
import Select from 'rise-ui/components/select.gjs';
import UniqueIdEl from 'rise-ui/components/unique-id-el.gjs';
import ENV from 'rise-ui/config/environment.js';
import strings from 'rise-ui/config/strings.js';
import event from 'rise-ui/helpers/event.js';
import scrollTo from 'rise-ui/modifiers/scroll-to.js';
import { ajax } from 'rise-ui/utils/ajax-rise.js';
import { encodeArrayFormField } from 'rise-ui/utils/form.js';
import { buildUrl } from 'rise-ui/utils/ajax.js';

const isOther = value => typeof value === 'string' && value.toLowerCase() === 'other';
const hasOther = selection => selection.find(option => isOther(option.value));

const API = ENV.APP.API.DATA;

export function getContactUrl() {
  return buildUrl([ API.URL , API.NAMESPACE , 'contact' ]);
}

const errors = {
  captcha:            'contact-error-captcha',
  'captcha-response': 'contact-error-captcha-fail',
  mail:               'contact-error-mail',
};

const MATCH_EMAIL =
  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

export default RouteTemplate(class ContactTemplate extends Component {
  @service('debug/contact') contactDebug;
  @service notifications;

  @tracked isSubmitting = false;

  @tracked name;
  @tracked subject;
  @tracked email;
  @tracked topic;
  @tracked topicOther;
  @tracked message;
  @tracked entitySelectedOptions;
  @tracked entityOther;
  @tracked applicationSelectedOptions;
  @tracked applicationOther;
  @tracked sectorSelectedOptions;
  @tracked themeOther;
  @tracked displaySuccess = false;

  constructor() {
    super(...arguments);
    this.notifications.setDefaultAutoClear(true);
    this.clearForm();
  }

  resetController(isExiting) {
    if (isExiting) {
      this.clearForm();
    }
  }

  get isTopicOther() {
    return isOther(this.topic);
  }
  get isEntityOther() {
    return hasOther(this.entitySelectedOptions);
  }
  get isThemeOther() {
    return hasOther(this.sectorSelectedOptions);
  }
  get isApplicationOther() {
    return hasOther(this.applicationSelectedOptions);
  }
  get isSubmitDisabled() {
    return this.isSubmitting;
  }

  clearForm() {
    Object.assign(this, {
      name: '',
      subject: '',
      email: '',
      topic: null,
      topicOther: '',
      message: '',
      entitySelectedOptions: [],
      entityOther: '',
      applicationSelectedOptions: [],
      applicationOther: '',
      sectorSelectedOptions: [],
      themeOther: '',
    });
  }

  @action updateTopic(selected) {
    this.topic = selected;
  }

  @action onSubmit() {
    this.displaySuccess = false;
    this.isSubmitting = true;
    let isSubmitted = false;

    if (this.isTopicOther ? !this.topicOther : !this.topic) {
      this.notifications.error(strings['contact-error-topic-required']);
    } else if (!this.subject) {
      this.notifications.error(strings['contact-error-subject-required']);
    } else if (!this.message) {
      this.notifications.error(strings['contact-error-message-required']);
    } else if (this.email && !MATCH_EMAIL.test(this.email)) {
      this.notifications.error(strings['contact-error-email']);
    } else {
      isSubmitted = true;
      // TODO figure out when to show this
      // notifications.error('Please complete the captcha.');

      // Firefox fails to load the script in local dev
      const { grecaptcha } = window;
      if (!grecaptcha || this.reCaptchaResponse) {
        this.submitForm();
      } else {
        grecaptcha.execute();
      }
    }
    if (!isSubmitted) {
      this.isSubmitting = false;
    }
  }

  async submitForm() {
    try {
      await ajax.post(getContactUrl(), {
        form: {
          name: this.name,
          email: this.email,
          subject: this.subject,
          message: this.message,
          topic: this.isTopicOther ? this.topicOther : this.topic,
          entitySelectedOptions: encodeArrayFormField(this.entitySelectedOptions,
            ({ value }) => this.isEntityOther && isOther(value)
              ? this.entityOther : value
          ),
          sectorSelectedOptions: encodeArrayFormField(this.sectorSelectedOptions,
            ({ value }) => this.isThemeOther && isOther(value)
              ? this.themeOther : value
          ),
          applicationSelectedOptions: encodeArrayFormField(this.applicationSelectedOptions,
            ({ value }) => this.isApplicationOther && isOther(value)
              ? this.applicationOther : value
          ),
          'g-recaptcha-response': this.reCaptchaResponse,
          ...(this.contactDebug.dryRun ? { dryRun: true } : {})
        },
      });
      this.reCaptchaResponse = null;
      this.displaySuccess = true;
      this.clearForm();
    } catch (err) {
      const reason = errors[err];
      if (!reason) {
        console.error(err);
      }
      this.notifications.error(strings[reason || 'contact-error-unknown']);
    }
    this.isSubmitting = false;
  }

  @action onCaptchaResolved(reCaptchaResponse) {
    this.reCaptchaResponse = reCaptchaResponse;
    this.submitForm();
  }

  @action
  onCaptchaExpired() {
    this.reCaptchaResponse = null;
  }

  @action
  onCaptchaError() {
    this.isSubmitting = false;
  }

  <template>
    {{pageTitle "Contact the RISE Team"}}

    <div class="id-contact">
      <h3 class="c-page-title id-contact__page-title qa-page-title">Contact Us</h3>
      <div class="id-contact__help c-help">
        <p>
          Your feedback lets us know about any problems you are experiencing with the
          RISE website or data, questions you have about using the RISE website or data,
          and helps guide future enhancements for RISE.
        </p>
        <p>
          Let us know what you think of the site. We'd like to know whether you were able
          to find the data you needed, what other data and features you'd like to see,
          how you use the data and web portal, and how we can improve RISE.
        </p>
        <p>
          IF YOU SEND US EMAIL<br>
          You may choose to provide us with personal information, as in e-mail with
          a comment or question. We use the information to improve our service to
          you or to respond to your request. Sometimes your email may be forwarded
          to other government employees who may be better able to help you. Except
          for authorized law enforcement investigations, we do not share your e-mail
          with any other outside organizations.
        </p>
        <p>
          Use the contact form below or email us at <a href="mailto:data@usbr.gov">data@usbr.gov</a>.
        </p>
      </div>
      {{#if this.displaySuccess}}
        <div class="id-contact__success" {{scrollTo}}>
          Thank you for your submission. A RISE team member will review your message
          and respond to you as soon as possible. Thank you!
        </div>
      {{/if}}
      <UniqueIdEl @tag="div" class="id-contact__input--name" as | id |>
        <label class="id-contact__input-label--name" for={{id}}><h3>Name (optional)</h3></label>
        <br><Input id=id class="id-contact__input-control--name" @value={{this.name}} />
      </UniqueIdEl>
      <UniqueIdEl @tag="div" class="id-contact__input--email" as | id |>
        <label class="id-contact__input-label--email" for={{id}}><h3>Email (optional)</h3></label>
        <br><Input id=id class="id-contact__input-control--email" @value={{this.email}} />
      </UniqueIdEl>
      <UniqueIdEl @tag="div" class="id-contact__input--topic" as | id |>
        <label class="id-contact__input-label--topic" for={{id}}><h3>Topic (required)</h3></label>
        <br>
        <Select id={{id}} class="id-contact__input-control--topic"
          onchange={{event this.updateTopic}}
          @selected={{this.topic}} as | select |>
          <select.Placeholder>Select a Topic</select.Placeholder>
          {{#each @model.topics as | topic |}}
            <select.Option @value={{topic}}>{{topic}}</select.Option>
          {{/each}}
        </Select>
      </UniqueIdEl>
      <div class="id-contact__response-warning c-help">
        Your email address and name are not mandatory, but we will not be able to respond
        to you regarding your comment or request without at least your email address.
      </div>
      {{#if this.isTopicOther}}
        <UniqueIdEl @tag="div" class="id-contact__input--topic-other" as | id |>
          <label for={{id}} class="id-contact__input-label--topic-other"><h3>Other Topic (required)</h3></label>
          <Input id={{id}} class="id-contact__input-control--topic-other"
            @value={{this.topicOther}}
          />
        </UniqueIdEl>
      {{/if}}
      <UniqueIdEl @tag="div" class="id-contact__input--subject" as | id |>
        <label for={{id}} class="id-contact__input-label--subject"><h3>Subject (required)</h3></label>
        <Input id={{id}} class="id-contact__input-control--subject"
          @value={{this.subject}}
        />
      </UniqueIdEl>
      <UniqueIdEl @tag="div" class="id-contact__input--message" as | id |>
        <label for={{id}} class="id-contact__input-label--message"><h3>Message (required)</h3></label>
        <Textarea id={{id}} class="id-contact__input-control--message"
          cols="80" rows="6"
          @value={{this.message}}
        />
      </UniqueIdEl>
      <div class="id-contact__input-group--entity">
        <label class="id-contact__input-group-label--entity">Entity (optional)</label>
        <MultiselectCheckboxes
          class="id-contact__input-group-controls--entity"
          @options={{@model.entityOptions}}
          @selection={{this.entitySelectedOptions}}
          @labelProperty="text"
        />
        {{#if this.isEntityOther}}
          <UniqueIdEl @tag="div" class="id-contact__input-group--entity-other" as | id |>
            <label for={{id}} class="id-contact__input-label--entity-other">Other Entity</label>
            <Input id={{id}} class="id-contact__input-control--entity-other"
              @value={{this.entityOther}}
            />
          </UniqueIdEl>
        {{/if}}
      </div>
      <div class="id-contact__input-group--theme">
        <label class="id-contact__input-group-label--theme">Theme (optional)</label>
        <MultiselectCheckboxes
          class="id-contact__input-group-controls--theme"
          @options={{@model.sectorOptions}}
          @selection={{this.sectorSelectedOptions}}
          @labelProperty="text"
        />
        {{#if this.isThemeOther}}
          <UniqueIdEl @tag="div" class="id-contact__input-group--theme-other" as | id |>
            <label for={{id}} class="id-contact__input-label--theme-other">Other Theme</label>
            <Input id={{id}} class="id-contact__input-control--theme-other"
              @value={{this.themeOther}}
            />
          </UniqueIdEl>
        {{/if}}
      </div>
      <div class="id-contact__input-group--application">
        <label class="id-contact__input-group-label--application">Application (optional)</label>
        <MultiselectCheckboxes
          class="id-contact__input-group-controls--application"
          @options={{@model.applicationOptions}}
          @selection={{this.applicationSelectedOptions}}
          @labelProperty="text"
        />
        {{#if this.isApplicationOther}}
          <UniqueIdEl @tag="div" class="id-contact__input-group--application-other" as | id |>
            <label for={{id}} class="id-contact__input-label--application-other">Other Application</label>
            <Input id={{id}} class="id-contact__input-control--application-other"
              @value={{this.applicationOther}}
            />
          </UniqueIdEl>
        {{/if}}
      </div>
      <div class="id-contact__captcha">
        <GRecaptcha
          @onSuccess={{this.onCaptchaResolved}}
          @onExpired={{this.onCaptchaExpired}}
          @onError={{this.onCaptchaError}}
          @size="invisible" @badge="inline"
        />
      </div>
      <div class="id-contact__submit">
        <CButton class="id-contact__submit-control"
          type="submit"
          @type="primary"
          @action={{this.onSubmit}}
          @disabled={{this.isSubmitDisabled}}
        >
          {{#if this.isSubmitDisabled}}<CIcon @icon="loading"/>{{/if}}
          Submit
        </CButton>
      </div>
      <div class="id-contact__privacy c-help">
        DOI Privacy Policy:
        <LinksTo @urlOpen="https://www.doi.gov/privacy">https://www.doi.gov/privacy</LinksTo>
      </div>
    </div>
    {{outlet}}
  </template>
});
